import { useState, useEffect, useCallback, useRef } from 'react';
import { getCategories, refreshCategories, buildCategoryHierarchy } from '../services/categoryService';
import type { ApiCategory, CategoryWithSubcategories } from '../types/category';
import { getCategoryIcon, getCategoryGradient } from '../utils/categoryIconMapper';

interface UseCategoriesReturn {
  categories: CategoryWithSubcategories[];
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  getCategoryById: (id: string) => CategoryWithSubcategories | undefined;
  getCategoryBySlug: (slug: string) => CategoryWithSubcategories | undefined;
}

// In-memory cache for instant access across components
let globalCategoryCache: CategoryWithSubcategories[] | null = null;
let globalPromise: Promise<ApiCategory[]> | null = null;

export function useCategories(): UseCategoriesReturn {
  const [categories, setCategories] = useState<CategoryWithSubcategories[]>(globalCategoryCache || []);
  const [isLoading, setIsLoading] = useState(!globalCategoryCache);
  const [error, setError] = useState<string | null>(null);
  const isMounted = useRef(true);
  const hasLoaded = useRef(false);

  // Load categories on mount
  useEffect(() => {
    isMounted.current = true;

    // If global cache already exists, sync to state immediately
    if (globalCategoryCache) {
      setCategories(globalCategoryCache);
      setIsLoading(false);
      hasLoaded.current = true;
      return;
    }

    // Prevent duplicate loads (StrictMode safety)
    if (hasLoaded.current) return;
    hasLoaded.current = true;

    const load = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Deduplicate: if a request is already in flight, reuse it
        let apiCategories: ApiCategory[];
        if (globalPromise) {
          apiCategories = await globalPromise;
        } else {
          globalPromise = getCategories();
          apiCategories = await globalPromise;
          globalPromise = null;
        }

        if (!isMounted.current) return;

        const transformed = buildTransformedCategories(apiCategories);
        globalCategoryCache = transformed;
        setCategories(transformed);
      } catch (err) {
        if (!isMounted.current) return;
        setError(err instanceof Error ? err.message : 'Failed to load categories');
      } finally {
        if (isMounted.current) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      isMounted.current = false;
    };
  }, []);

  // Refresh function (forces API call, bypasses cache)
  const refresh = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const apiCategories = await refreshCategories();
      if (!isMounted.current) return;
      const transformed = buildTransformedCategories(apiCategories);
      globalCategoryCache = transformed;
      setCategories(transformed);
    } catch (err) {
      if (!isMounted.current) return;
      setError(err instanceof Error ? err.message : 'Failed to refresh categories');
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  }, []);

  // Get category by ID
  const getCategoryById = useCallback((id: string): CategoryWithSubcategories | undefined => {
    return categories.find(c => c.id === id);
  }, [categories]);

  // Get category by slug
  const getCategoryBySlug = useCallback((slug: string): CategoryWithSubcategories | undefined => {
    return categories.find(c => c.slug === slug);
  }, [categories]);

  return {
    categories,
    isLoading,
    error,
    refresh,
    getCategoryById,
    getCategoryBySlug,
  };
}

// Build transformed categories with hierarchy
const buildTransformedCategories = (apiCategories: ApiCategory[]): CategoryWithSubcategories[] => {
  const { parents, childrenByParent } = buildCategoryHierarchy(apiCategories);
  
  return parents.map(parent => {
    const children = childrenByParent[parent.id] || [];
    
    return {
      ...parent,
      Icon: getCategoryIcon(parent.slug),
      gradient: getCategoryGradient(parent.slug),
      subcategories: children.map(child => ({
        id: child.id,
        name: child.name,
        slug: child.slug,
        description: child.description,
      })),
    };
  });
};

// Prefetch hook for route preloading
export function usePrefetchCategories(): () => void {
  const prefetch = useCallback(() => {
    if (!globalCategoryCache && !globalPromise) {
      globalPromise = getCategories().then(apiCategories => {
        globalCategoryCache = buildTransformedCategories(apiCategories);
        return apiCategories;
      }).finally(() => {
        globalPromise = null;
      });
    }
  }, []);

  return prefetch;
}

// Static accessor for non-component usage
export const getCachedCategories = (): CategoryWithSubcategories[] | null => {
  return globalCategoryCache;
};

// Preload on app init
export const preloadCategories = (): void => {
  if (!globalCategoryCache && !globalPromise) {
    globalPromise = getCategories().then(apiCategories => {
      globalCategoryCache = buildTransformedCategories(apiCategories);
      return apiCategories;
    }).catch(() => {
      // Silently fail on preload
      return [];
    }).finally(() => {
      globalPromise = null;
    });
  }
};
